import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField, ToggleSwitch, Button } from '../../primitives';
import { getCsrfToken } from '../../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * EventCreationForm
 * Reusable event creation form (UI matches the provided mock).
 *
 * - Can be embedded inside a Dialog (Events page) or inside the wizard (DataBuilder step 3).
 * - By default it does NOT render actions. Set `showActions` for Cancel/Save buttons.
 * - Parent can trigger submission via `registerSubmit`.
 */
const EventCreationForm = ({
  resourceId,
  onSaved,
  onCancel,
  showActions = false,
  submitLabel = 'Save',
  registerSubmit,
  onCanSubmitChange,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    date: null,
    start_time: null,
    end_time: null,
    cost: '',
    requires_full_equipment: false,
    is_open_to_public: false,
    resource: resourceId,
    is_recurring: false,
    recurring_start_date: null,
    recurring_end_date: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Keep resource in sync if parent changes it
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      resource: resourceId,
    }));
  }, [resourceId]);

  const combineDateAndTime = (date, time) => {
    if (!date || !time) return null;
    try {
      const combined = new Date(date);
      combined.setHours(time.getHours());
      combined.setMinutes(time.getMinutes());
      combined.setSeconds(0);
      combined.setMilliseconds(0);
      return combined;
    } catch (err) {
      return null;
    }
  };

  const formatDateTimeForAPI = (date) => {
    if (!date) return '';
    try {
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString();
      }
      return '';
    } catch (err) {
      return '';
    }
  };

  const formatDateForAPI = (date) => {
    if (!date) return '';
    try {
      if (date instanceof Date && !isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    } catch (err) {
      return '';
    }
  };

  const canSubmit = useMemo(() => {
    if (!resourceId) return false;
    if (!formData.name?.trim()) return false;
    if (!formData.date) return false;
    if (!formData.start_time) return false;
    if (!formData.end_time) return false;
    if (formData.is_recurring) {
      if (!formData.recurring_start_date) return false;
      if (!formData.recurring_end_date) return false;
    }
    return !loading;
  }, [resourceId, formData, loading]);

  useEffect(() => {
    if (onCanSubmitChange) onCanSubmitChange(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (errorMessage) setErrorMessage('');
  };

  const handleDateChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      date: value,
      recurring_start_date: prev.is_recurring ? value : prev.recurring_start_date,
    }));
    if (errors.date || errors.start_time || errors.end_time) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.date;
        delete next.start_time;
        delete next.end_time;
        return next;
      });
    }
    if (errorMessage) setErrorMessage('');
  };

  const handleTimeChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (errorMessage) setErrorMessage('');
  };

  const handleRecurringToggle = (value) => {
    setFormData((prev) => ({
      ...prev,
      is_recurring: value,
      recurring_start_date: value ? (prev.date || prev.recurring_start_date) : null,
      recurring_end_date: value ? prev.recurring_end_date : null,
    }));
    if (errors.recurring_start_date || errors.recurring_end_date) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.recurring_start_date;
        delete next.recurring_end_date;
        return next;
      });
    }
    if (errorMessage) setErrorMessage('');
  };

  const handleRecurringDateChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (errorMessage) setErrorMessage('');
  };

  const submit = async () => {
    if (!canSubmit) return false;
    setLoading(true);
    setErrorMessage('');
    setErrors({});

    try {
      const startDateTime = combineDateAndTime(formData.date, formData.start_time);
      const endDateTime = combineDateAndTime(formData.date, formData.end_time);

      const requestData = {
        name: formData.name,
        start_time: formatDateTimeForAPI(startDateTime),
        end_time: formatDateTimeForAPI(endDateTime),
        cost: parseFloat(formData.cost) || 0,
        requires_full_equipment: formData.requires_full_equipment,
        is_open_to_public: formData.is_open_to_public,
        resource: resourceId,
      };

      if (formData.is_recurring) {
        requestData.recurring_start_date = formatDateForAPI(formData.recurring_start_date);
        requestData.recurring_end_date = formatDateForAPI(formData.recurring_end_date);
      }

      const csrfToken = getCsrfToken();
      const headers = { 'Content-Type': 'application/json' };
      if (csrfToken) headers['X-CSRFToken'] = csrfToken;

      const response = await fetch(`${API_BASE_URL}/events/`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (
          data.name ||
          data.start_time ||
          data.end_time ||
          data.cost ||
          data.resource ||
          data.recurring_start_date ||
          data.recurring_end_date
        ) {
          setErrors(data);
        } else {
          setErrorMessage(data.error || data.message || 'Failed to create event. Please try again.');
        }
        setLoading(false);
        return false;
      }

      if (onSaved) onSaved(Array.isArray(data) ? data[0] : data);
      setLoading(false);
      return true;
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      // eslint-disable-next-line no-console
      console.error('Error creating event:', err);
      setLoading(false);
      return false;
    }
  };

  // Let parent trigger submit (wizard Done)
  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(submit);
  }, [registerSubmit, submit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submit();
  };

  const handleClose = () => {
    if (!loading && onCancel) onCancel();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Typography
          variant="body1"
          sx={{
            mb: 2,
            color: '#000000',
            fontWeight: 600,
          }}
        >
          Add your event details
        </Typography>

        <TextField
          fullWidth
          placeholder="Event Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          margin="normal"
          autoComplete="off"
        />

        <DatePicker
          value={formData.date}
          onChange={handleDateChange}
          slotProps={{
            textField: {
              fullWidth: true,
              margin: 'normal',
              placeholder: 'Date',
              error: !!errors.date,
              helperText: errors.date,
            },
          }}
        />

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TimePicker
              value={formData.start_time}
              onChange={(newValue) => handleTimeChange('start_time', newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  placeholder: 'Start Time',
                  error: !!errors.start_time,
                  helperText: errors.start_time,
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TimePicker
              value={formData.end_time}
              onChange={(newValue) => handleTimeChange('end_time', newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  placeholder: 'End Time',
                  error: !!errors.end_time,
                  helperText: errors.end_time,
                },
              }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#000000', fontSize: '0.875rem' }}>
            Is this a recurring event?
          </Typography>
          <ToggleSwitch value={formData.is_recurring} onChange={handleRecurringToggle} />
        </Box>

        {formData.is_recurring && (
          <Box sx={{ mt: 2 }}>
            <DatePicker
              value={formData.recurring_start_date}
              onChange={(newValue) => handleRecurringDateChange('recurring_start_date', newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  placeholder: 'Recurring Start Date',
                  error: !!errors.recurring_start_date,
                  helperText: errors.recurring_start_date,
                },
              }}
            />
            <DatePicker
              value={formData.recurring_end_date}
              onChange={(newValue) => handleRecurringDateChange('recurring_end_date', newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  placeholder: 'Recurring End Date',
                  error: !!errors.recurring_end_date,
                  helperText: errors.recurring_end_date,
                },
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 2, width: '50%' }}>
          <TextField
            fullWidth
            placeholder="Event Cost"
            name="cost"
            type="number"
            value={formData.cost}
            onChange={handleChange}
            error={!!errors.cost}
            helperText={errors.cost}
            margin="normal"
            inputProps={{ step: '0.01', min: '0' }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.requires_full_equipment}
                onChange={handleChange}
                name="requires_full_equipment"
              />
            }
            label="Event Requires Full Equipment"
          />
          <FormControlLabel
            control={
              <Checkbox checked={formData.is_open_to_public} onChange={handleChange} name="is_open_to_public" />
            }
            label="Event is open to the public"
          />
        </Box>

        {showActions && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  <span>Saving...</span>
                </Box>
              ) : (
                submitLabel
              )}
            </Button>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default EventCreationForm;

